import type { AdvertisedRateResult, GaragePermitRate, GarageResult } from '@/types/reserve.types';
import type { GarageCardProps } from '@/components/reserve/garage-card';
import type { MonthlyPlanName, MonthlyRateSelection } from '@/types/monthly-rate-selection.types';
import { ReservationMode } from '@/components/reserve/search-bar/SearchBar.types';
import { ceilToMinute, formatStayDuration } from '@/lib/date-time-utils';
import { isGarageWithinRange } from '@/lib/garage-hours-utils';
import {
  filterPermitRatesByEligibility,
  filterPermitRatesByVehicleType,
  isRateForVehicleType,
  parkingPassPackageTotal,
} from '@/lib/permit-rate-filter';
import { shouldHideIntroRate, type EligibilityContext } from '@/lib/intro-rate-eligibility';
import { computeProcessingFeeFromPricing } from '@/lib/processing-fee-utils';
import type { CouponPricing } from '@/components/reserve/garage-detail-panel/GarageDetailPanel.types';
import { filterRatesForPermitLimit } from '@/lib/multiple-permit-gate';
import { MY_ACCOUNT_ROUTES } from '@/routes/routes.constants';

/**
 * Walking pace for the product results' location / group lines (useProductResults). PB-8219
 * removed walk time from the garage search card, map pop-ups and detail panel only.
 */
export const WALK_MINUTES_PER_MILE = 20;

/**
 * The guest's selected hourly window carried through a mid-booking login so the
 * authenticated re-search prices the same duration the guest saw. `enter`/`exit`
 * are the REQUESTED instants (not the same-price extended exit) — they stay the
 * source of truth for the refetch + booking, and the PB-1491 extension is
 * re-derived from the fresh search. See {@link buildPostLoginReturnUrl}.
 */
export interface PostLoginHourlyWindow {
  enter?: Date | null;
  exit?: Date | null;
  vehicle?: string;
  vehicleId?: string | null;
}

/**
 * Return URL for a mid-booking login. Rates can be account-specific, so a guest
 * must not land on the payment page with the guest-priced store state after
 * signing in — instead they return to the listing (which re-searches under
 * their token) with `detail`/`detailTab` so the panel they booked from reopens.
 *
 * `feeIds` carries fee DOCUMENT ids, not positional `fee-N` ids — see
 * {@link mapFeeDocumentIdsToPositional}.
 *
 * A direct-garage visit (`?garageId=`) restores its own panel from the URL, but
 * the URL-sync effect never writes `enter`/`exit` in that mode
 * (useReservationSearch URL-sync guard), so without help the return URL is
 * dateless and the post-login re-search falls back to the theme default
 * duration — surfacing a wrong (flat/1-hour) price after login (PB-4625). When
 * an `hourlyWindow` is supplied, its enter/exit/vehicle are pinned onto the
 * passed-through direct-garage query so the authenticated re-search prices the
 * guest's actual window.
 */
const POST_LOGIN_DETAIL_RESTORE_FLAG = 'pb-post-login-detail-restore';

/**
 * Whether the post-login detail restore may run yet.
 *
 * ## Invariants:
 * - **`isApplyingCoupon` gates this exactly as `isSearching` does, and leaving it out is the
 *   defect this function exists to make impossible to reintroduce.** The restore resolves a
 *   rate id against `garageResults`; a coupon-revealed card only appears there once the
 *   coupon-bearing search has RESOLVED. `applyCoupon` sets `isApplyingCoupon` synchronously
 *   before its first await, and the search hook subscribes to the whole store, so kicking off
 *   the re-apply immediately re-renders — and because the effect's `setSelection` dep is a
 *   fresh arrow each render, the effect re-runs at once. Without this flag the restore then
 *   ran against the PRE-coupon results, missed the revealed card, and silently selected the
 *   cheapest public rate instead — the customer returns from logging in on a dearer rate, and
 *   the restore params are stripped so nothing can correct it.
 * - Extracted rather than left inline because three review rounds each patched a symptom of
 *   this missing gate — an early-return that never cleared, then an unbounded re-apply loop,
 *   then a premature restore. All three were the same absent condition. A named predicate is
 *   the only form of it that a test can hold still.
 * - Pure and total, so the decision is testable without the hook's harness, which cannot
 *   currently reach this effect: `hasSearched` is owned by the location-search hook and needs
 *   a real geocoded search to become true.
 */
export function canRestoreDetailNow(s: {
  hasSearched: boolean;
  isSearching: boolean;
  isApplyingCoupon: boolean;
  hasGarages: boolean;
}): boolean {
  return s.hasSearched && !s.isSearching && !s.isApplyingCoupon && s.hasGarages;
}

export function buildPostLoginReturnUrl(
  garageId: string,
  tab: 'hourly' | 'monthly',
  rateId?: string,
  feeIds?: string[],
  hourlyWindow?: PostLoginHourlyWindow,
  resultsCouponCode?: string | null
): string {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('garageId')) {
    params.delete('rateId');
    params.delete('feeIds');
    params.set('detail', garageId);
    params.set('detailTab', tab);
    if (rateId) params.set('rateId', rateId);
    if (feeIds?.length) params.set('feeIds', feeIds.join(','));
    // PB-5582, and the same shape as the hourly-window pin below: a coupon-revealed rate card
    // exists only in a response to a request carrying the code, so without this the
    // authenticated re-search cannot see the rate the guest selected and the restore falls
    // through to the cheapest public one. Its own param, never the per-garage `coupon`.
    params.delete('resultsCoupon');
    if (resultsCouponCode) params.set('resultsCoupon', resultsCouponCode);
    try {
      sessionStorage.setItem(POST_LOGIN_DETAIL_RESTORE_FLAG, '1');
    } catch {
      // Storage unavailable (privacy mode) — restore falls back to replace-strip.
    }
  } else if (hourlyWindow) {
    // PB-4625: direct-garage mode never syncs enter/exit into the URL, so pin the
    // guest's selected window here — otherwise the post-login re-search re-seeds
    // the theme default duration and re-quotes at the wrong price.
    if (hourlyWindow.enter) params.set('enter', hourlyWindow.enter.toISOString());
    if (hourlyWindow.exit) params.set('exit', hourlyWindow.exit.toISOString());
    if (hourlyWindow.vehicle) params.set('vehicle', hourlyWindow.vehicle);
    if (hourlyWindow.vehicleId) params.set('vehicleId', hourlyWindow.vehicleId);
  }
  const query = params.toString();
  return query ? `${MY_ACCOUNT_ROUTES.reserve}?${query}` : MY_ACCOUNT_ROUTES.reserve;
}

/**
 * True when this tab reached the `?detail=` URL through the same-tab login
 * round-trip (flag set at Book time); consuming clears it. Gate for stripping
 * the params via history.back(): only safe when the previous entry is known
 * to be our own pre-login listing — in a restored session or pasted URL it
 * could be another site entirely.
 */
/**
 * Maps fee DOCUMENT ids carried through a mid-booking login back to a rate's positional
 * `fee-N` ids.
 *
 * ## Invariants:
 * - An id absent from `optionalFees` is dropped, never remapped positionally. Why: the
 *   post-login search runs authenticated and can offer a different set, so a positional
 *   remap would silently select a fee the customer never picked (the PB-3789 hazard).
 * - Apply the result only to the rate the ids were carried for — on another rate they are
 *   meaningless.
 */
export function mapFeeDocumentIdsToPositional(
  optionalFees: Array<{ feeId?: string }> | undefined,
  feeDocumentIds: string[]
): string[] {
  if (!optionalFees?.length || feeDocumentIds.length === 0) return [];
  const wanted = new Set(feeDocumentIds);
  return optionalFees
    .map((fee, index) => ({ fee, index }))
    .filter(({ fee }) => !!fee.feeId && wanted.has(fee.feeId))
    .map(({ index }) => `fee-${index}`);
}

export function consumePostLoginRestoreFlag(): boolean {
  try {
    const wasSet = sessionStorage.getItem(POST_LOGIN_DETAIL_RESTORE_FLAG) === '1';
    sessionStorage.removeItem(POST_LOGIN_DETAIL_RESTORE_FLAG);
    return wasSet;
  } catch {
    // Storage unavailable — treat as a foreign tab and take the safe path.
    return false;
  }
}

/**
 * Whether the requested hourly window falls inside the garage's operating hours.
 * Legacy enforces this via `isGarageWithinRange`; without it the redesign let customers
 * book past closing time, after which the API silently truncated the exit. Fail-open when
 * either date is missing. `allowOvernightReservations` arrives nested in
 * `transientPricingConfig` from the V2 search assembler. (PB-3060)
 */
export function isWithinOperatingHours(
  garage: GarageResult,
  enterDate: Date | null | undefined,
  exitDate: Date | null | undefined
): boolean {
  if (!enterDate || !exitDate) return true;
  return isGarageWithinRange(
    {
      hours: garage.hours,
      allowOvernightReservations: garage.transientPricingConfig?.allowOvernightReservations,
    },
    enterDate,
    exitDate
  );
}

/**
 * Whether a card sorts below every bookable garage, in the list and in the map
 * viewport partition alike — legacy `orderGarages` parity (its first key demotes
 * `hourlyParkingSoldOut || !rateInfo`; its monthly key demotes sold-out/unpayable).
 * `price === null` is not implied by `!bookable`: a garage can pass every flag and
 * still have no rate for the window. Zones are exempt, as in legacy.
 */
export function sortsBelowBookable(card: GarageCardProps): boolean {
  if (card.isZone) return false;
  return !card.bookable || card.price === null;
}

/** How closely a garage's name matches the searched destination — lower ranks first. */
export enum GarageNameMatch {
  exact = 0,
  partial = 1,
  none = 2,
}

/** Shorter than this, a query or name is too generic to rank by (e.g. "a", "1"). */
const MIN_NAME_MATCH_LENGTH = 3;

/** Lowercases, folds accents, drops apostrophes (straight and curly) and other punctuation. */
function normalizeForNameMatch(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['‘’ʼ`]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Ranks a garage by how well its name matches the Destination text the parker searched (PB-4567).
 *
 * The text is compared whole and by its first comma-separated segment, which is the place name of
 * a Google prediction ("Revathy's Garage, New York, NY, USA").
 *
 * ## Invariants:
 * - A name match outranks distance. Why: the search point comes from Google, which does not know
 *   most of our garages. A parker who types a garage's name gets an unrelated point, every garage
 *   lands at a near-identical distance, and the one they named can sit anywhere in the list.
 * - An exact match outranks a partial one, so "UIO" still puts "UIO" above "UIO 66 Rusteze Racing".
 * - Text shorter than {@link MIN_NAME_MATCH_LENGTH} never matches. Why: a one- or two-character
 *   query is contained in most names and would reorder the whole list.
 */
export function garageNameMatchRank(garageName: string, destinationText: string): GarageNameMatch {
  const name = normalizeForNameMatch(garageName);
  const fullText = normalizeForNameMatch(destinationText);
  const placeName = normalizeForNameMatch(destinationText.split(',')[0]);
  if (name.length < MIN_NAME_MATCH_LENGTH || placeName.length < MIN_NAME_MATCH_LENGTH) {
    return GarageNameMatch.none;
  }
  if (name === placeName || name === fullText) return GarageNameMatch.exact;
  if (name.includes(placeName) || placeName.includes(name)) return GarageNameMatch.partial;
  return GarageNameMatch.none;
}

// Lives in lib so the reserve tab switchers (components/) can share it with the list.
export { isGarageListedInMode } from '@/lib/mode-availability';

/**
 * Returns true if a garage can be booked for the given reservation mode.
 * Composes garage `bookable` flag with the per-mode master switches that the
 * legacy app honoured: type-specific online sales, sold-out, integration,
 * and the standard hourly/monthly enablement flag.
 *
 * Mirrors enableMonthlyTab/enableHourlyTab logic from legacy `constants.ts`,
 * minus the active-permit-count + disableMultiplePermitBooking pieces which
 * live in a separate composite-gating ticket.
 */
export function isGarageBookable(garage: GarageResult, mode: ReservationMode): boolean {
  if (!garage.bookable) return false;
  if (mode === ReservationMode.monthly) {
    if (!garage.monthlyEnabled) return false;
    if (garage.valetManagerMonthlyIntegration === false) return false;
    if (garage.onlineMonthlyParkingSales === false) return false;
    if (garage.monthlyParkingSoldOut === true) return false;
    return true;
  }
  if (!garage.hourlyEnabled) return false;
  if (garage.onlineTransientParkingSales === false) return false;
  if (garage.hourlyParkingSoldOut === true) return false;
  return true;
}

/**
 * Returns the URL the Book button should open in a new tab, or null when
 * the garage uses the standard in-app booking flow. Empty strings are treated
 * as not set — backend assembler defaults the field to "".
 *
 * Validates the URL is parseable and uses http/https. Defense-in-depth against
 * a misconfigured backend ever returning `javascript:`/`data:` schemes (open
 * redirect / OWASP A10).
 *
 * Monthly only: legacy never hands an hourly booking to the external system.
 */
export function resolveExternalBookingUrl(
  garage: GarageResult,
  mode: ReservationMode
): string | null {
  if (mode !== ReservationMode.monthly) return null;
  const url = garage.ecommerceExternalUrl?.trim();
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    return parsed.href;
  } catch {
    return null;
  }
}

/** 1-minute slack so sub-minute clock skew doesn't produce a spurious offer. */
const SAME_PRICE_TOLERANCE_MS = 60_000;

/**
 * Same-price exit extension for a garage's hourly rate, or null when none.
 * `entryExitWindows[vt].exitBefore` is the latest exit the booked rate covers;
 * when it is past the requested exit the customer can stay longer for free.
 * Falls back to the STANDARD window, matching `mapGarageResultToDetail`.
 */
export function resolveSamePriceExtension(
  garage: GarageResult,
  vehicleType: string,
  enterDate: Date,
  requestedExit: Date
): { extendedExit: Date; maxStayMinutes: number } | null {
  const windows = garage.entryExitWindows;
  if (!windows) return null;
  const window = windows[vehicleType] ?? windows['STANDARD'];
  const exitBeforeStr = window?.exitBefore;
  if (!exitBeforeStr) return null;

  const extendedExit = new Date(exitBeforeStr);
  if (Number.isNaN(extendedExit.getTime())) return null;
  if (extendedExit.getTime() <= requestedExit.getTime() + SAME_PRICE_TOLERANCE_MS) {
    return null;
  }

  const maxStayMinutes = Math.round((extendedExit.getTime() - enterDate.getTime()) / 60_000);
  return { extendedExit, maxStayMinutes };
}

/**
 * Same-price extension as display-ready values (extended exit + compact duration label),
 * or null when none applies.
 *
 * PB-8094: the returned `extendedExit` is the DISPLAY exit, not the raw window boundary. A
 * clock-block window ends at the last covered instant (enter + Nh − 1s, e.g. 10:44:59), so
 * surfacing it raw read 10:44 / 1:59 while the duration said 2h. `ceilToMinute` presents the window
 * by its exclusive upper bound (10:45 / 2:00) — the same transform the confirmed pass and every
 * other exit surface use, so the whole summary agrees. It never shows an exit earlier than the
 * window actually covers, since ceilToMinute(x) ≥ x. Values sent to the api / used in calculations
 * stay raw.
 */
export function getSamePriceExtensionDisplay(
  garage: GarageResult,
  vehicleType: string,
  enterDate: Date,
  requestedExit: Date
): { extendedExit: Date; durationText: string } | null {
  const extension = resolveSamePriceExtension(garage, vehicleType, enterDate, requestedExit);
  if (!extension) return null;
  const displayExit = ceilToMinute(extension.extendedExit);
  return {
    extendedExit: displayExit,
    durationText: formatStayDuration(enterDate, displayExit, { inclusiveEnd: false }),
  };
}

/**
 * Same-price extension carried by an applied special-rate/coupon lookup.
 * `rateOutputWithDiscount.validUntil` is the latest exit the winning rate
 * covers; legacy drives the Exit-By timeline from it while still booking the
 * requested window (the server auto-extends the exit). The garage search never
 * sees the rateId, so `garage.entryExitWindows` cannot express this window.
 *
 * PB-8094: `validUntil` is the last covered instant — `enter + Nh − 1s` (e.g. a 2h "Hours Free"
 * coupon from 08:45 yields 10:44:59). Surfacing it raw made the summary show 10:44 / 1:59 while
 * the duration banner said 2h. The returned `extendedExit` is `ceilToMinute(validUntil)` — the
 * window's exclusive upper bound (10:45 / 2:00), matching the confirmed pass. Exit, "Paying until",
 * and the chip all derive from it, so they agree with the banner; ceilToMinute(x) ≥ x so it never
 * shows an exit earlier than the rate covers.
 */
export function getCouponSamePriceExtensionDisplay(
  validUntil: string | undefined,
  enterDate: Date,
  requestedExit: Date
): { extendedExit: Date; durationText: string } | null {
  if (!validUntil) return null;
  const validUntilDate = new Date(validUntil);
  if (Number.isNaN(validUntilDate.getTime())) return null;
  if (validUntilDate.getTime() <= requestedExit.getTime() + SAME_PRICE_TOLERANCE_MS) {
    return null;
  }
  const displayExit = ceilToMinute(validUntilDate);
  return {
    extendedExit: displayExit,
    durationText: formatStayDuration(enterDate, displayExit, { inclusiveEnd: false }),
  };
}

/** Maps amenity API keys to Reserve namespace translation keys */
/**
 * Occupancy status code -> `Reserve` translation key.
 *
 * Shared by every surface that renders an occupancy indicator: the location cards
 * (`useGarageResults`) and the grouped-product headers (`useProductResults`, PB-5346). A code with
 * no entry here renders no indicator at all, which is why a second copy of this map would be a
 * silent divergence rather than a visible one — the surface missing a code just shows nothing.
 */
export const OCCUPANCY_STATUS_KEYS: Record<string, string> = {
  AVAILABLE: 'occupancyAvailable',
  FILLING_FAST: 'occupancyFillingFast',
  NEAR_CAPACITY: 'occupancyNearCapacity',
  RESERVATIONS_CLOSED: 'occupancySoldOut',
};

export const AMENITY_LABEL_KEYS: Record<string, string> = {
  VALET: 'amenityValet',
  SELFPARK: 'amenitySelfParking',
  WHEELCHAIRACCESSIBLE: 'amenityWheelchair',
  COVEREDLOT: 'amenityCoveredGarage',
  OPENLOT: 'amenityOpenLot',
  INDOORS: 'amenityIndoors',
  OUTDOORS: 'amenityOutdoors',
  ELECTRICCHARGING: 'amenityElectricCharging',
  RENTALCARS: 'amenityRentalCars',
  BICYCLES: 'amenityBicycles',
  MOTORCYCLES: 'amenityMotorcycles',
  SUVOVERSIZED: 'amenitySuvOversized',
  TRUCKSUPERSIZED: 'amenityTruckSupersized',
  ONSITESTAFF: 'amenityOnSiteStaff',
  ZONES: 'amenityZones',
};

/**
 * Monthly listing card price + CTA from the server-resolved advertised rate
 * (PB-365 / PB-3556), replacing the client-side `resolveMonthlyCardPricing`
 * reduction (PB-3227).
 *
 * ## Invariants:
 * - The displayed rate comes ONLY from the server's `advertisedRate` resolution
 *   (PB-365 AC Technical-3) — never re-picked client-side from
 *   permitRates/monthlyRates; both booking sites must show the same number.
 *   Exception: `gateIntroRates` re-resolves an advertised intro rate the customer cannot have.
 * - The parking-pass package is not a rate card and is outside the server
 *   resolution; it stays a client-side fallback so pass-only garages keep a
 *   bookable price instead of a dead-end Sold Out card (PB-3015).
 *
 * @since 2026-07-17
 */
export function advertisedCardPricing(garage: GarageResult): {
  price: number | null;
  applyWaitlist: boolean;
} {
  const advertised = garage.advertisedRate;
  if (advertised?.rate != null) {
    return { price: advertised.rate, applyWaitlist: advertised.applyWaitlist === true };
  }
  const parkingPassPrice = parkingPassPackageTotal(garage.guestPassInfo ?? garage.guestPassSetting);
  if (parkingPassPrice > 0) {
    // No per-rate flag on the pass package — the facility-level signal classifies.
    return { price: parkingPassPrice, applyWaitlist: garage.waitlistInfo?.available === true };
  }
  return { price: null, applyWaitlist: false };
}

/**
 * PB-365: the detail-panel monthly selection matching the garage's server-resolved advertised
 * rate, so the PERMIT DETAILS panel pre-selects the same rate advertised on the list card.
 * Matches the advertised rate to a permit rate by rateCardId (or specialRateId). Returns null
 * when the garage has no advertised rate or it isn't a permit rate in the list — callers fall
 * back to findCheapestMonthlyRate.
 */
export function findAdvertisedMonthlyRate(
  garage: GarageResult,
  canBookNewPermit?: boolean
): MonthlyRateSelection | null {
  const advertised = garage.advertisedRate;
  if (!advertised) return null;
  // PUBLIC ONLY by design (PB-5582): the advertised rate is the garage's own headline price.
  // A coupon-revealed card is never advertised — it exists precisely because it is not shown
  // publicly — so widening this would let an unlocked rate masquerade as the garage's shopfront.
  // PB-8404: at the permit limit only an exempt rate may be pre-selected.
  const match = filterRatesForPermitLimit(garage.permitRates ?? [], canBookNewPermit).find(
    (r) =>
      (advertised.rateCardId && r.rateCardId === advertised.rateCardId) ||
      (advertised.specialRateId && r.specialRateId === advertised.specialRateId)
  );
  if (!match || (!match.rateCardId && !match.specialRateId)) return null;
  return {
    kind: 'permitRateCard',
    rateCardId: match.rateCardId ?? match.specialRateId ?? '',
    specialRateId: match.specialRateId,
  };
}

// The processing fee is charged on the payable (discounted) amount, never on the pre-coupon rate.
export function resolveCouponPricing(
  rateOutput: { rate: number; amountToPay: number; chargeToAccountAmount?: number } | undefined,
  isDeepLink: boolean,
  pricing: Parameters<typeof computeProcessingFeeFromPricing>[0]
): CouponPricing | undefined {
  if (!rateOutput) return undefined;
  const net = rateOutput.amountToPay;
  // A FLAT fee must not resurrect a free booking.
  const processingFee = net >= 0.01 ? computeProcessingFeeFromPricing(pricing, net) : 0;
  return {
    mode: isDeepLink ? 'override' : 'split',
    rate: rateOutput.rate,
    chargeToAccountAmount: rateOutput.chargeToAccountAmount ?? 0,
    processingFee,
    total: net >= 0.01 ? net + processingFee : 0,
  };
}

export function findCheapestMonthlyRate(
  garage: GarageResult,
  vehicleType: string,
  canBookNewPermit?: boolean
): MonthlyRateSelection | null {
  // PUBLIC ONLY by design (PB-5582). This is the FALLBACK used when a selection cannot be
  // resolved, so including revealed cards would let it substitute an unlocked rate the customer
  // never chose — the silent swap the post-login restore fix exists to prevent, arriving from
  // the other direction.
  const validPermitRates = filterRatesForPermitLimit(
    garage.permitRates ?? [],
    canBookNewPermit
  ).filter((r) => r.rateCardId || r.specialRateId);
  if (validPermitRates.length > 0) {
    const vehicleRates = validPermitRates.filter((r) => isRateForVehicleType(r, vehicleType));
    // Never falls through to monthly plans, which a permit garage does not list.
    if (vehicleRates.length === 0) return null;
    // PB-3227: prefer the cheapest bookable rate so the pre-selected rate matches the
    // card's Book-first priority; fall back to waitlist only when all rates are waitlist.
    const bookableRates = vehicleRates.filter((r) => r.applyWaitlist !== true);
    const pool = bookableRates.length > 0 ? bookableRates : vehicleRates;
    const cheapest = pool.reduce((min, r) =>
      (r.rate ?? Infinity) < (min.rate ?? Infinity) ? r : min
    );
    return {
      kind: 'permitRateCard',
      rateCardId: cheapest.rateCardId ?? cheapest.specialRateId ?? '',
      specialRateId: cheapest.specialRateId,
    };
  }

  const monthlyRates = canBookNewPermit === false ? undefined : garage.monthlyRates;
  if (monthlyRates && Object.keys(monthlyRates).length > 0) {
    // Fallback chain mirrors legacy getMinimumRate: requested → STANDARD → first available.
    const resolvedVt = monthlyRates[vehicleType]
      ? vehicleType
      : monthlyRates['STANDARD']
        ? 'STANDARD'
        : Object.keys(monthlyRates)[0];
    const list = monthlyRates[resolvedVt];
    if (!list?.length) return null;
    const cheapest = list.reduce((min, mr) =>
      (mr.onlineRate ?? Infinity) < (min.onlineRate ?? Infinity) ? mr : min
    );
    return {
      kind: 'monthlyPlan',
      planName: (cheapest.planName ?? 'DEFAULT') as MonthlyPlanName,
      rate: cheapest.onlineRate ?? 0,
      vehicleType: resolvedVt,
    };
  }

  return null;
}

type PermitRateUser = Parameters<typeof filterPermitRatesByEligibility>[2];

const permitRateKey = (r: { rateCardId?: string; specialRateId?: string }) =>
  r.rateCardId ?? r.specialRateId ?? '';

// Re-picks a hidden advertised intro rate; flags aren't sent, so flagged picks fall to cheapest.
export function gateIntroRates(
  garage: GarageResult,
  ctx: EligibilityContext,
  user: PermitRateUser,
  vehicleType: string
): GarageResult {
  // PUBLIC ONLY by design: coupon-revealed cards are never intro-gated.
  const rates = garage.permitRates ?? [];
  const hiddenKeys = new Set(
    rates.filter((r) => shouldHideIntroRate(r, garage.garageId, ctx)).map(permitRateKey)
  );
  if (hiddenKeys.size === 0) return garage;

  const kept = rates.filter((r) => !hiddenKeys.has(permitRateKey(r)));
  const advertised = garage.advertisedRate;
  const advertisedIsHidden =
    (!!advertised?.rateCardId && hiddenKeys.has(advertised.rateCardId)) ||
    (!!advertised?.specialRateId && hiddenKeys.has(advertised.specialRateId));

  return {
    ...garage,
    permitRates: kept,
    advertisedRate: advertisedIsHidden
      ? resolveAdvertisedFallback(kept, garage.company ?? '', user, vehicleType)
      : advertised,
  };
}

// Java's Comparator.nullsLast(naturalOrder()) — the resolver's tie-break.
function compareIdsNullsLast(a: string | undefined, b: string | undefined): number {
  if (a == null || b == null) return a == null ? (b == null ? 0 : 1) : -1;
  return a < b ? -1 : a > b ? 1 : 0;
}

function resolveAdvertisedFallback(
  rates: GaragePermitRate[],
  company: string,
  user: PermitRateUser,
  vehicleType: string
): AdvertisedRateResult | undefined {
  const candidates = filterPermitRatesByVehicleType(
    filterPermitRatesByEligibility(
      rates.filter((r) => (r.rateCardId || r.specialRateId) && r.rate != null),
      company,
      user
    ),
    vehicleType
  );
  if (candidates.length === 0) return undefined;
  const bookable = candidates.filter((r) => r.applyWaitlist !== true);
  const pool = bookable.length > 0 ? bookable : candidates;
  const winner = [...pool].sort(
    (a, b) =>
      (a.rate ?? 0) - (b.rate ?? 0) ||
      compareIdsNullsLast(a.rateCardId, b.rateCardId) ||
      compareIdsNullsLast(a.specialRateId, b.specialRateId)
  )[0];
  return {
    rate: winner.rate,
    rateCardId: winner.rateCardId,
    specialRateId: winner.specialRateId,
    name: winner.name,
    applyWaitlist: bookable.length === 0,
    fromAdvertisedFlag: false,
  };
}

/**
 * Finds a garage in an array by matching garageId, locationCode, websiteName, or name.
 * The identifier is decoded before matching.
 */
export function findGarageByIdentifier(
  garages: GarageResult[],
  identifier: string
): GarageResult | undefined {
  const decoded = decodeURIComponent(identifier);
  const normalise = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  return garages.find(
    (g) =>
      g.garageId === decoded ||
      g.locationCode === decoded ||
      normalise(g.websiteName ?? '') === normalise(decoded) ||
      normalise(g.name ?? '') === normalise(decoded)
  );
}
